import React, { FC, useRef, useState } from 'react';

import { ComposeNodeType } from '../../containers/compose/props';
import useIsComposeOpen from '../../hooks/router/search/useIsComposeOpen';
import useClickOutside from '../../hooks/useClickOutside';
import translate from '../../i18n/translate';
import MuiIcon from '../MUI/MuiIcon';

import ComposeActions from './ComposeActions';
import * as Styled from './styled';

type Props = {
  nodeType: ComposeNodeType;
};

/**
 * Button that opens compose popup on mobiles
 *
 * @param props          Props passed to component
 * @param props.nodeType Type of the node
 *
 * @returns              Button react component
 */
const OpenComposeButton: FC<Props> = ({ nodeType }) => {
  const [isOpen, setIsOpen] = useState(false);
  const isComposeFormOpen = useIsComposeOpen();
  const ref = useRef<HTMLDivElement | null>(null);

  useClickOutside({
    /**
     * Close menu on clicking outside
     *
     * @returns void
     */
    onClick: () => setIsOpen(false),
    ref,
    stopExec: isComposeFormOpen || !isOpen,
  });

  /**
   * Toggle compose actions
   *
   * @returns void
   */
  const toggleOpen = () => setIsOpen(prev => !prev);

  /**
   * Handles click on link and toggles compose actions
   */
  const handleLinkClick = () => {
    toggleOpen();
  };

  return (
    <div ref={ref}>
      <ComposeActions
        handleClick={handleLinkClick}
        nodeType={nodeType}
        shouldShow={isOpen && !isComposeFormOpen}
      />
      {!isComposeFormOpen && (
        <Styled.ToggleButton
          aria-label={translate(isOpen ? 'GENERAL__CLOSE' : 'GENERAL__OPEN')}
          color="tertiary"
          data-is-open={isOpen}
          onClick={toggleOpen}
          variant="contained"
        >
          <MuiIcon icon="add" />
        </Styled.ToggleButton>
      )}
    </div>
  );
};

export default OpenComposeButton;
